export type SpringParams = {
  stiffness: number;
  damping: number;
  mass: number;
  precision: number;
  allowOvershoot: boolean;
};

export type SpringState = {
  position: number;
  velocity: number;
  target: number;
};

/**
 * Advance a spring simulation by `dt` seconds. Mutates and returns `state`.
 *
 * Uses semi-implicit Euler integration of `F = -kx - cv`.
 * When `allowOvershoot` is false, snaps to target on sign flip of displacement.
 */
export const stepSpring = (
  state: SpringState,
  dt: number,
  params: SpringParams,
): SpringState => {
  const displacementBefore = state.position - state.target;
  const springForce = -params.stiffness * displacementBefore;
  const dampingForce = -params.damping * state.velocity;
  const acceleration = (springForce + dampingForce) / params.mass;

  state.velocity += acceleration * dt;
  state.position += state.velocity * dt;

  if (!params.allowOvershoot) {
    const displacementAfter = state.position - state.target;
    const overshot =
      displacementBefore !== 0 &&
      Math.sign(displacementAfter) !== Math.sign(displacementBefore);
    if (overshot) {
      state.position = state.target;
      state.velocity = 0;
    }
  }

  return state;
};

export const isSpringSettled = (
  state: SpringState,
  precision: number,
): boolean =>
  Math.abs(state.velocity) < precision &&
  Math.abs(state.target - state.position) < precision;

/**
 * Run a spring simulation offline at fixed dt and return position samples.
 * Used to preview the trajectory without driving the DOM.
 */
export const simulateSpring = ({
  from,
  to,
  params,
  durationSeconds = 4,
  dt = 1 / 120,
}: {
  from: number;
  to: number;
  params: SpringParams;
  durationSeconds?: number;
  dt?: number;
}): { t: number; value: number }[] => {
  const state: SpringState = { position: from, velocity: 0, target: to };
  const samples: { t: number; value: number }[] = [{ t: 0, value: from }];
  const steps = Math.ceil(durationSeconds / dt);

  for (let i = 1; i <= steps; i++) {
    stepSpring(state, dt, params);
    samples.push({ t: i * dt, value: state.position });
    if (isSpringSettled(state, params.precision)) break;
  }

  return samples;
};
